import axios from 'axios'
import hash from 'object-hash'

import { chartPalette } from '../'
import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataBase,
  CompassAPIMultiSeriesChartData,
  convertCompassToHighchartsDataFormat,
  getCompassAPIChartData,
  getDataSources,
  getMultipleLineChartProps,
} from '../utils/compass'
import { Palette, datetimeXAxis } from '../utils/constants'
import { capitalizeString } from '../utils/string'
import type { BaseContext } from './'
import { CommonChartConfig } from './common'

const SHORT_SQUEEZE_CHARTS_PATH = '/api/charts/short-squeeze'

type TradingComponentId = 'us' | 'bz'

type UsTradingRadarChartId = 'indice' | 'radar_group_scores' | 'radar_scores'

type UsTradingRegularChartId =
  | 'volume'
  | 'return_price'
  | 'excess_return_peers'
  | 'excess_return_index'
  | 'option_volume'
  | 'option_open_interest'
  | 'short_interest'
  | 'short_interest_ratio'
  | 'reddit'
  | 'reddit_mean'
  | 'score'

type UsTradingChartId = UsTradingRadarChartId | UsTradingRegularChartId

type BzTradingRadarChartId = 'indice' | 'radar_grouped_scores' | 'radar_score'

type BzTradingRegularChartId =
  | 'daily_volume'
  | 'return'
  | 'daily_returns'
  | 'excess_return_index'
  | 'excess_return_peers_synthetic_index'
  | 'options_volume'
  | 'options_open_interest'
  | 'options_pin'
  | 'short_interest'
  | 'short_interest_ratio'
  | 'score'

type RadarScoresCategory =
  | 'return_price'
  | 'price_er_index'
  | 'price_er_peers'
  | 'daily_volume'
  | 'interval_volume'
  | 'option_volume'
  | 'option_open_interest'
  | 'short_interest'
  | 'short_interest_ratio'
  | 'reddit'
  | 'reddit_mean'

type RadarGroupScoresCategory =
  | 'performance'
  | 'volume'
  | 'options'
  | 'short'
  | 'reddit'

type RadarScoreCategory =
  | 'daily_returns'
  | 'excess_return_index'
  | 'excess_return_peers_synthetic_index'
  | 'daily_volume'
  | 'short_interest'
  | 'short_interest_ratio'
  | 'options_pin'
  | 'intraday_volume'
  | 'options_volume'
  | 'options_open_interest'

type RadarGroupedScoresCategory = 'performance' | 'volume' | 'options' | 'short'

type BzTradingChartId = BzTradingRadarChartId | BzTradingRegularChartId

const US_TRADING_CHARTS_PATHS: Record<UsTradingChartId, string> = {
  indice: '/trading-us/indice',
  radar_group_scores: '/trading-us/radar-group-scores',
  radar_scores: '/trading-us/radar-scores',
  volume: '/trading-us/volume',
  return_price: '/trading-us/return-price',
  excess_return_peers: '/trading-us/excess-return-peers',
  excess_return_index: '/trading-us/excess-return-index',
  option_volume: '/trading-us/option-volume',
  option_open_interest: '/trading-us/option-open-interest',
  short_interest: '/trading-us/short-interest',
  short_interest_ratio: '/trading-us/short-interest-ratio',
  reddit: '/trading-us/reddit',
  reddit_mean: '/trading-us/reddit-mean',
  score: '/trading-us/score',
}

const BZ_TRADING_CHARTS_PATHS: Record<BzTradingChartId, string> = {
  indice: '/trading-bz/indice',
  radar_grouped_scores: '/trading-bz/radar-grouped-score',
  radar_score: '/trading-bz/radar-score',
  daily_volume: '/trading-bz/daily-volume',
  return: '/trading-bz/return',
  daily_returns: '/trading-bz/daily-returns',
  excess_return_index: '/trading-bz/excess-return-index',
  excess_return_peers_synthetic_index:
    '/trading-bz/excess-return-peers-synthetic-index',
  options_volume: '/trading-bz/options-volume',
  options_open_interest: '/trading-bz/options-open-interest',
  options_pin: '/trading-bz/options-pin',
  short_interest: '/trading-bz/short-interest',
  short_interest_ratio: '/trading-bz/short-interest-ratio',
  score: '/trading-bz/score',
}

const categoryLabelRadarGroupScores: Record<
  RadarGroupScoresCategory,
  string
> = {
  performance: 'Performance',
  volume: 'Quantity',
  options: 'Options',
  short: 'Short',
  reddit: 'Reddit',
}
const categoryLabelRadarScores: Record<RadarScoresCategory, string> = {
  return_price: 'Return',
  price_er_index: 'ER Index',
  price_er_peers: 'ER Peers',
  daily_volume: 'Quantity',
  interval_volume: 'Intraday Quantity',
  option_volume: 'Option Quantity',
  option_open_interest: 'Option Open Interest Quantity',
  short_interest: 'Short/Float',
  short_interest_ratio: 'Short/ADTV',
  reddit: 'Reddit Relevance',
  reddit_mean: 'Reddit Mean Relevance',
}

const categoryLabelRadarGroupedScores: Record<
  RadarGroupedScoresCategory,
  string
> = {
  performance: 'Performance',
  volume: 'Quantity',
  options: 'Options',
  short: 'Short',
}

const categoryLabelRadarScore: Record<RadarScoreCategory, string> = {
  daily_returns: 'Return',
  excess_return_index: 'ER Index',
  excess_return_peers_synthetic_index: 'ER Peers',
  daily_volume: 'Quantity',
  short_interest: 'Short/Float',
  short_interest_ratio: 'Short/ADTV',
  options_pin: 'Option Pin',
  intraday_volume: 'Intraday Quantity',
  options_volume: 'Option Quantity',
  options_open_interest: 'Option Open Interest Quantity',
}

export namespace ShortSqueeze {
  export namespace Macro {
    const CONTEXT_ID = 'equity:short_squeeze:macro'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'nasdaq_composite_total_volume'
        | 'nyse_composite_total_volume'
        | 'cboe_total_call_volume'
        | 'cboe_total_put_volume'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getChartPropsWrapper(config, data),
    }

    const getChartPropsWrapper = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData
    ) =>
      getMultipleLineChartProps(Context, {
        addMinMaxIndicators: true,
        chartTitle:
          config.chart === 'nasdaq_composite_total_volume'
            ? 'NASDAQ composite total volume'
            : config.chart === 'nyse_composite_total_volume'
            ? 'NYSE composite total volume'
            : config.chart === 'cboe_total_call_volume'
            ? 'CBOE total call volume'
            : 'CBOE total put volume',
        msdIndicators: { avg: true, sd1: true, sd2: true },
      })(config, data)
  }

  export namespace Radar {
    const CONTEXT_ID = 'equity:short_squeeze:radar'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      type: TradingComponentId
      chart: UsTradingChartId | BzTradingChartId
      date: string
      ticker: string
      context: typeof CONTEXT_ID
    }

    interface ChartData extends CompassAPIDataBase {
      title: string
      subtitle: string
      data: {
        score_name: string
        value: number
      }[]
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url:
          SHORT_SQUEEZE_CHARTS_PATH +
          (config.type === 'us'
            ? US_TRADING_CHARTS_PATHS[config.chart as UsTradingChartId]
            : BZ_TRADING_CHARTS_PATHS[config.chart as BzTradingChartId]),
        method: 'POST',
        data: { date: config.date, ticker: config.ticker },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = [
        {
          data: data.data.map((point) => [point.score_name, point.value]) ?? [],
          id: `radar-series`,
          fillOpacity: 0.5,
          marker: { enabled: false },
          showInLegend: false,
          type: 'area',
          yAxis: 'default',
        },
      ]

      const creditsConfig = getDataSources(data.data_sources)

      const xAxis = [
        {
          categories:
            data.data.map((option: any) =>
              config.chart === 'radar_group_scores'
                ? categoryLabelRadarGroupScores[
                    option.score_name as RadarGroupScoresCategory
                  ]
                : config.chart === 'radar_scores'
                ? categoryLabelRadarScores[
                    option.score_name as RadarScoresCategory
                  ]
                : config.chart === 'radar_grouped_scores'
                ? categoryLabelRadarGroupedScores[
                    option.score_name as RadarGroupedScoresCategory
                  ]
                : categoryLabelRadarScore[
                    option.score_name as RadarScoreCategory
                  ]
            ) ?? [],
          pointPlacement: 'on',
        },
      ]

      const yAxis = {
        gridLineInterpolation: 'circle' as const,
        id: 'default',
        lineWidth: 0,
        min: 0,
      }

      const tooltip = {
        shared: true,
        split: undefined,
        formatter: function (this: {
          x: number
          points: {
            y: number
            series: { name: string; color: string }
          }[]
        }) {
          return `${this.x} ${this.points.map(
            (point) => `${point.y.toFixed(2)}`
          )}`
        },
      }

      return {
        id: Context.getChartId(config),
        title: config.title ?? data.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            chart: { polar: true, type: 'line' },
            colors: chartPalette,
            series,
            tooltip,
            subtitle: { text: data.subtitle },
            xAxis,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }

  export namespace HistoryScore {
    const CONTEXT_ID = 'equity:short_squeeze:history_score'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      type: TradingComponentId
      ticker: string
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartData = async (config: ChartConfig) =>
      axios({
        url:
          SHORT_SQUEEZE_CHARTS_PATH +
          (config.type === 'us' ? '/trading-us/indice' : '/trading-bz/indice'),
        method: 'GET',
        params: { ticker: config.ticker },
      }).then((response) => response.data)

    const getChartPropsWrapper = (config: ChartConfig, data: ChartData) =>
      getMultipleLineChartProps(
        Context,
        {
          useRegularChart: true,
          chartTitle: `Score history (${config.ticker})`,
          xAxis: datetimeXAxis[0],
        },
        { type: Array(data.data.length).fill('spline') }
      )(config, data)

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps: getChartPropsWrapper,
    }
  }

  export namespace Trading {
    const CONTEXT_ID = 'equity:short_squeeze:trading'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      title?: string
      type: TradingComponentId
      chart: BzTradingRegularChartId | UsTradingRegularChartId
      ticker: string
      context: typeof CONTEXT_ID
    }

    export interface PlotLine {
      pos_two_dp: number
      pos_one_dp: number
      mean: number
      neg_one_dp: number
      neg_two_dp: number
    }

    interface SeriesData extends PlotLine {
      name: string
      data: {
        date: string
        value: number
      }[]
    }

    interface ChartData extends CompassAPIDataBase {
      data: SeriesData[]
    }

    const getChartData = async (config: ChartConfig) =>
      axios({
        url:
          SHORT_SQUEEZE_CHARTS_PATH +
          (config.type === 'us'
            ? US_TRADING_CHARTS_PATHS[config.chart as UsTradingChartId]
            : BZ_TRADING_CHARTS_PATHS[config.chart as BzTradingChartId]),
        method: 'GET',
        params: { ticker: config.ticker },
      }).then((response) => response.data)

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const isPercentual = (
        type: TradingComponentId,
        chart: BzTradingRegularChartId | UsTradingRegularChartId
      ) =>
        type === 'us'
          ? chart === 'return_price' ||
            chart === 'excess_return_peers' ||
            chart === 'excess_return_index' ||
            chart === 'short_interest'
          : chart === 'return' ||
            chart === 'daily_returns' ||
            chart === 'excess_return_index' ||
            chart === 'excess_return_peers_synthetic_index'

      const showHours = (
        type: TradingComponentId,
        chart: BzTradingRegularChartId | UsTradingRegularChartId
      ) =>
        type === 'us'
          ? chart === 'volume' ||
            chart === 'return_price' ||
            chart === 'excess_return_peers' ||
            chart === 'excess_return_index'
          : chart === 'daily_volume' ||
            chart === 'return' ||
            chart === 'daily_returns' ||
            chart === 'excess_return_index'

      const hasMSDIndicators = (
        type: TradingComponentId,
        chart: BzTradingRegularChartId | UsTradingRegularChartId
      ) =>
        type === 'us'
          ? chart === 'volume' ||
            chart === 'return_price' ||
            chart === 'excess_return_peers' ||
            chart === 'excess_return_index'
          : chart === 'daily_volume' || chart === 'return'

      const series = data.data.map((s, idx) => ({
        ...(hasMSDIndicators(config.type, config.chart)
          ? {
              color: idx === 0 ? Palette.darkBlue : '#000',
              dashStyle: idx === 0 ? 'Solid' : 'Dash',
            }
          : {}),
        data: convertCompassToHighchartsDataFormat(s.data, {
          convertPercentage: isPercentual(config.type, config.chart),
        }),
        id: `series-${idx}`,
        name: capitalizeString(s.name.split('_').join(' ')),
        ...(config.chart === 'score'
          ? {
              showInLegend: true,
              visible:
                s.name === 'daily_volume' ||
                s.name === 'return_price' ||
                s.name === 'short_interest',
            }
          : { showInLegend: false }),
        marker: { enabled: false },
        tooltip: {
          ...(isPercentual(config.type, config.chart)
            ? { valueSuffix: '%' }
            : {}),
          ...(!showHours(config.type, config.chart)
            ? { xDateFormat: '%A, %b %e, %Y' }
            : {}),
          valueDecimals: 2,
        },
        type: 'spline',
        yAxis: 'default',
      }))

      const creditsConfig = getDataSources(data.data_sources)

      const getPlotLine = (
        type: keyof PlotLine,
        value: number,
        isPercentual: boolean
      ) => ({
        color: '#707E83',
        dashStyle: 'Dash',
        width: 1,
        value: isPercentual ? value * 100 : value,
        zIndex: 10,
        label: {
          text: `${
            (type === 'pos_two_dp'
              ? '2'
              : type === 'pos_one_dp'
              ? '1'
              : type === 'neg_one_dp'
              ? '-1'
              : '2') + 'σ'
          }: ${(isPercentual ? value * 100 : value).toFixed(3)}${
            isPercentual ? '%' : ''
          }`,
        },
      })

      const plotLinesValues: PlotLine = {
        pos_two_dp: data.data[1]?.pos_two_dp ?? 0,
        pos_one_dp: data.data[1]?.pos_one_dp ?? 0,
        mean: data.data[1]?.mean ?? 0,
        neg_one_dp: data.data[1]?.neg_one_dp ?? 0,
        neg_two_dp: data.data[1]?.neg_two_dp ?? 0,
      }

      const plotLines = Object.entries(plotLinesValues)
        .filter((plotLine) => plotLine[0] !== 'mean')
        .map((plotLine) =>
          getPlotLine(
            plotLine[0] as keyof PlotLine,
            plotLine[1],
            isPercentual(config.type, config.chart)
          )
        )

      const areAllPlotLinesEqual = (plotLines: PlotLine) =>
        plotLines.pos_two_dp === plotLines.mean &&
        plotLines.pos_one_dp === plotLines.mean &&
        plotLines.neg_one_dp === plotLines.mean &&
        plotLines.neg_two_dp === plotLines.mean

      const shouldShowPlotLines =
        !areAllPlotLinesEqual(plotLinesValues) &&
        data &&
        hasMSDIndicators(config.type, config.chart)

      const yAxis = {
        id: 'default',
        opposite: false,
        title: { text: '' },
        ...(isPercentual(config.type, config.chart)
          ? { labels: { format: '{value}%' } }
          : {}),
        ...(shouldShowPlotLines ? { plotLines } : {}),
      }

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            series,
            colors: chartPalette,
            legend: { maxHeight: 80 },
            tooltip: { shared: false, split: true },
            xAxis: datetimeXAxis,
            yAxis: yAxis,
          },
        ]),
        stockTools: true,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }
}
